import React, { createContext, useContext, useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

export type Language = 'en' | 'es' | 'zh' | 'fr' | 'ja';

type LanguageProviderProps = {
  children: React.ReactNode;
  defaultLanguage?: Language;
  storageKey?: string;
};

type LanguageProviderState = {
  language: Language;
  setLanguage: (language: Language) => void;
  availableLanguages: { value: Language; label: string }[];
};

const initialState: LanguageProviderState = {
  language: 'en',
  setLanguage: () => null,
  availableLanguages: [
    { value: 'en', label: 'English' },
    { value: 'es', label: 'Español' },
    { value: 'zh', label: '中文' },
    { value: 'fr', label: 'Français' },
    { value: 'ja', label: '日本語' },
  ],
};

const LanguageProviderContext = createContext<LanguageProviderState>(initialState);

export function LanguageProvider({
  children,
  defaultLanguage = 'en',
  storageKey = 'language',
  ...props
}: LanguageProviderProps) {
  const { i18n } = useTranslation();
  const [language, setLanguageState] = useState<Language>(() => {
    // Let i18next handle auto-detection on first load
    // It will check localStorage, navigator language, etc.
    const detectedLang = i18n.language as Language;
    const supportedLanguages: Language[] = ['en', 'es', 'zh', 'fr', 'ja'];
    
    // Validate detected language is supported
    if (supportedLanguages.includes(detectedLang)) {
      return detectedLang;
    }
    
    // Fallback to default only if detection failed
    return defaultLanguage;
  });

  useEffect(() => {
    const root = window.document.documentElement;
    root.setAttribute('lang', language);
    // Ensure i18n is synchronized with language state
    if (i18n.language !== language) {
      i18n.changeLanguage(language);
    }
  }, [language, i18n]);

  const setLanguage = (newLanguage: Language) => {
    localStorage.setItem(storageKey, newLanguage);
    setLanguageState(newLanguage);
    i18n.changeLanguage(newLanguage);
  };

  const value = useMemo(
    () => ({
      language,
      setLanguage,
      availableLanguages: initialState.availableLanguages,
    }),
    [language]
  );

  return (
    <LanguageProviderContext.Provider {...props} value={value}>
      {children}
    </LanguageProviderContext.Provider>
  );
}

export const useLanguage = () => {
  const context = useContext(LanguageProviderContext);

  if (context === undefined)
    throw new Error('useLanguage must be used within a LanguageProvider');

  return context;
};